import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../core/utils/api";
import {
  getAffiliatePortalBlockedMessage,
  isAffiliatePortalBlockedError,
} from "../utils/affiliatePortalMessages";

export const AffiliateAuthContext = createContext();

const emptyAuthState = {
  isAuthenticated: false,
  user: null, // {id, username, must_complete_onboarding?}
  // {required_version, document_id, accepted} — drives the portal terms gate
  terms: null,
  isChecking: true,
  error: null,
  isPortalBlocked: false,
  portalBlockedMessage: null,
};

export const AffiliateAuthProvider = ({ children }) => {
  const [affiliateAuth, setAffiliateAuth] = useState(emptyAuthState);

  const check = useCallback(async () => {
    setAffiliateAuth((p) => ({
      ...p,
      isChecking: true,
      error: null,
      isPortalBlocked: false,
      portalBlockedMessage: null,
    }));
    try {
      // suppressUnauthorizedEvent: this is an affiliate-specific session probe.
      // A 401 here must NOT fire the global user-auth "unauthorised" event,
      // which would incorrectly clear the regular user's auth state.
      const res = await apiFetch("/api/influencer/check/", {
        credentials: "include",
        suppressUnauthorizedEvent: true,
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.ok) {
        const user = data.user || null;
        setAffiliateAuth({
          isAuthenticated: true,
          user: user
            ? {
                ...user,
                must_complete_onboarding: !!user.must_complete_onboarding,
                has_had_access_coupons: !!user.has_had_access_coupons,
              }
            : null,
          terms: data.terms || null,
          isChecking: false,
          error: null,
          isPortalBlocked: false,
          portalBlockedMessage: null,
        });
      } else if (res.status === 403 && isAffiliatePortalBlockedError(data)) {
        setAffiliateAuth({
          isAuthenticated: false,
          user: null,
          terms: null,
          isChecking: false,
          error: null,
          isPortalBlocked: true,
          portalBlockedMessage: getAffiliatePortalBlockedMessage(data),
        });
      } else {
        setAffiliateAuth({
          isAuthenticated: false,
          user: null,
          terms: null,
          isChecking: false,
          error: data?.error || null,
          isPortalBlocked: false,
          portalBlockedMessage: null,
        });
      }
    } catch (e) {
      setAffiliateAuth({
        isAuthenticated: false,
        user: null,
        terms: null,
        isChecking: false,
        error: "Network error",
        isPortalBlocked: false,
        portalBlockedMessage: null,
      });
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!mounted) return;
      await check();
    })();
    return () => {
      mounted = false;
    };
  }, [check]);

  // If any affiliate API call returns 401/403, treat it as auth-expired and force re-login.
  useEffect(() => {
    const expiredHandler = () => {
      setAffiliateAuth({
        isAuthenticated: false,
        user: null,
        terms: null,
        isChecking: false,
        error: null,
        isPortalBlocked: false,
        portalBlockedMessage: null,
      });
    };
    const blockedHandler = (event) => {
      const data = event?.detail?.data;
      setAffiliateAuth({
        isAuthenticated: false,
        user: null,
        terms: null,
        isChecking: false,
        error: null,
        isPortalBlocked: true,
        portalBlockedMessage: getAffiliatePortalBlockedMessage(data),
      });
    };
    window.addEventListener("affiliate-auth-expired", expiredHandler);
    window.addEventListener("affiliate-portal-blocked", blockedHandler);
    return () => {
      window.removeEventListener("affiliate-auth-expired", expiredHandler);
      window.removeEventListener("affiliate-portal-blocked", blockedHandler);
    };
  }, []);

  const value = useMemo(
    () => ({
      affiliateAuth,
      setAffiliateAuth,
      refreshAffiliateAuth: check,
    }),
    [affiliateAuth, check]
  );

  return <AffiliateAuthContext.Provider value={value}>{children}</AffiliateAuthContext.Provider>;
};
